import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useGalleries } from '../contexts/GalleryContext';

// 플레이스홀더 이미지 URL
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&h=600&fit=crop&q=80';

const Gallery: React.FC = () => {
  const { galleries, loading } = useGalleries();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 이미지 클릭 핸들러 (상세 페이지로 이동)
  const handleImageClick = (galleryId: number) => {
    navigate(`/gallery/${galleryId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex items-center justify-center min-h-[400px] mt-20">
          <div className="text-sm sm:text-base text-gray-500">로딩 중...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header */}
      <div className="relative text-white py-12 sm:py-16 md:py-20 lg:py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=1920&h=600&fit=crop&q=80"
            alt="Gallery"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-gray-200 mb-2 sm:mb-3 text-xs sm:text-sm font-medium tracking-wider">참여마당</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">갤러리</h1>
          <div className="w-16 sm:w-20 h-1 bg-white/70"></div>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="flex-1 py-8 sm:py-10 md:py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 결과 카운트 */}
          <div className="mb-4 sm:mb-6">
            <p className="text-sm sm:text-base text-gray-600">
              총 <span className="font-bold text-green-600">{galleries.length}</span>개의 사진
            </p>
          </div>

          {galleries.length === 0 ? (
            <div className="text-center py-12 sm:py-16 md:py-20">
              <p className="text-gray-500 text-base sm:text-lg">갤러리가 비어있습니다.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
              {galleries.map((gallery) => {
                return (
                  <div
                    key={gallery.id}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                    onClick={() => handleImageClick(gallery.id)}
                  >
                    {/* 이미지 */}
                    <div className="relative h-48 sm:h-52 md:h-56 bg-gray-200 overflow-hidden">
                      <img
                        src={gallery.thumbnail_url || gallery.image_url || PLACEHOLDER_IMAGE}
                        alt={gallery.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />

                      {/* 오버레이 */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
                    </div>

                    {/* 내용 */}
                    <div className="p-3 sm:p-4">
                      <h3 className="text-sm sm:text-base font-bold text-gray-800 mb-1 sm:mb-2 line-clamp-1">
                        {gallery.title}
                      </h3>

                      {gallery.description && (
                        <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 line-clamp-2">
                          {gallery.description}
                        </p>
                      )}

                      <div className="text-xs sm:text-sm text-gray-500">
                        <span className="text-[10px] sm:text-xs">
                          {new Date(gallery.created_at).toLocaleDateString('ko-KR')}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Gallery;
